import React, { Suspense, lazy, useEffect, useMemo } from 'react';
import { createBrowserRouter, useSearchParams, Navigate, Outlet } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@mui/material';
import PageLoader from './ui-library/PageLoader';
import ChatContainer from './components/chat/ChatContainer';
import { AuthProvider, useAuth } from './components/auth/AuthProvider';
import { SignupContainer } from './components/signup/SignupContainer';
import LoginCallback from './components/auth/LoginCallback';
import { MobileLayoutProvider } from './contexts/MobileLayoutContext';
import { ChatProvider } from './contexts/ChatContext';
import { BallotRecordsProvider } from './contexts/BallotRecordsContext';
import { SurveyProvider } from './contexts/SurveyContext';
import useAnalytics from './hooks/useAnalytics';
import { useLanguageSync } from './hooks/useLanguageSync';
import { urlLanguageOverride, urlLanguageChangedActiveLanguage } from './i18n';
import { SettingsContainer } from './components/settings/SettingsContainer';
import { Login } from './components/Login';
import { Logout } from './components/Logout';
import { LoginError } from './components/LoginError';
import LandingPageRedirect from './components/LandingPageRedirect';
import { INTERNAL_ROUTES } from './constants/urls';
import { isEmbed } from './config/embed';
import { detectOs } from './utils/detectOs';

const HelpContainer = lazy(() => import('./components/help/HelpContainer'));

const routes = [
  { index: true, element: <LandingPageRedirect /> }, // Only fires at "/"
  { path: 'signup', element: <SignupContainer /> },
  { path: 'login-error', element: <LoginError /> },
  { path: 'login', element: <Login /> },
  { path: 'logout', element: <Logout /> },
  { path: 'callback', element: <LoginCallback /> },
  { path: 'chat/:id?', element: <ChatContainer /> },
  { path: 'settings', element: <SettingsContainer /> },
  { path: INTERNAL_ROUTES.HELP, element: <HelpContainer /> },
  { path: '*', element: <Navigate to="/" /> }, // Catch-all
];

const LanguageSync: React.FC = () => {
  useLanguageSync();
  return null;
};

// Syncs platform + auth state as Mixpanel super properties. Must render
// inside AuthProvider so useAuth() resolves.
const SuperPropertiesSync: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { registerSuperProperties } = useAnalytics();

  useEffect(() => {
    registerSuperProperties({
      platform: isMobile ? 'mobile' : 'desktop',
      // Partner-embed traffic is its own client so it never inflates (or
      // deflates) first-party web numbers in cross-client comparisons.
      client: isEmbed() ? 'embed_widget' : 'web',
      os: detectOs(),
      isGuest: !isAuthenticated,
    });
  }, [isMobile, isAuthenticated, registerSuperProperties]);

  return null;
};

// Wrapper component to provide auth context to main app routes
const AuthenticatedApp: React.FC = () => {
  const [searchParams] = useSearchParams();
  const campaignCode = useMemo(() => searchParams.get('campaign'), [searchParams]);
  const { track } = useAnalytics();

  useEffect(() => {
    if (track && campaignCode) {
      setTimeout(() => {
        track('Campaign Code Used', { campaignCode });
      }, 1000); // Artificial delay to ensure analytics is initialized
    }
  }, [track, campaignCode]);

  useEffect(() => {
    // A valid `?lang=` is applied by i18n's querystring detector at init; record
    // it once here so it lands for both guests and signed-in users — but only
    // when it actually changed the language, not when it matched the stored one.
    if (track && urlLanguageChangedActiveLanguage) {
      setTimeout(() => {
        track('Language Updated', { source: 'URL', language: urlLanguageOverride });
      }, 1000); // Artificial delay to ensure analytics is initialized
    }
  }, [track]);

  return (
    <AuthProvider>
      <SuperPropertiesSync />
      <LanguageSync />
      <ChatProvider>
        <MobileLayoutProvider>
          <BallotRecordsProvider>
            <SurveyProvider>
              <Suspense fallback={<PageLoader />}>
                <Outlet />
              </Suspense>
            </SurveyProvider>
          </BallotRecordsProvider>
        </MobileLayoutProvider>
      </ChatProvider>
    </AuthProvider>
  );
};

const App: React.FC = () => (
  <React.Fragment>
    <Suspense fallback={<PageLoader />}>
      <Outlet />
    </Suspense>
  </React.Fragment>
);

export const router = createBrowserRouter([
  {
    path: '/', // Parent route anchors children
    Component: AuthenticatedApp,
    children: routes,
  },
]);

export default App;
